import { ContextType } from "../../data/enums/ContextType";
import { HotKeyAction } from "../../data/HotKeyAction";
import { store } from "../../index";
import { updateActiveContext } from "../../store/general/actionCreators";
import { xor, isEmpty } from "lodash";
import { PopupContext } from "./PopupContext";
import { GeneralSelector } from "../../store/selectors/GeneralSelector";
import { EventType } from "../../data/enums/EventType";
import editorActionsHelper from "../../components/Helper/editorActionsHelper";
import { EditorActions } from "../actions/EditorActions";

const MODIFIER_KEYS = new Set(["Meta", "Control", "Alt", "Shift"]);

export class ContextManager {
    private static actions: HotKeyAction[] = [];
    private static contextHistory: ContextType[] = [];
    private static hotkeysConfig: Record<string, string[]> | null = null;

    public static getActiveCombo(): string[] {
        return [];
    }

    public static init(): void {
        window.addEventListener(EventType.KEY_DOWN, ContextManager.onDown);
        window.addEventListener(EventType.FOCUS, ContextManager.onFocus);
        ContextManager.updateHotkeys();
    }

    public static switchCtx(context: ContextType): void {
        const activeCtx: ContextType = GeneralSelector.getActiveContext();

        if (activeCtx !== context) {
            ContextManager.contextHistory.push(activeCtx);
            ContextManager.updateCtx(context);
        }
    }

    public static updateHotkeys(): void {
        ContextManager.actions = editorActionsHelper(ContextManager.hotkeysConfig);
    }

    public static setHotkeysConfig(hotkeys: Record<string, string[]> | null): void {
        ContextManager.hotkeysConfig = hotkeys;
        ContextManager.updateHotkeys();
    }

    public static getHotkeysConfig(): Record<string, string[]> | null {
        return ContextManager.hotkeysConfig;
    }

    private static updateCtx(context: ContextType): void {
        store.dispatch(updateActiveContext(context));
        switch (context) {
            case ContextType.EDITOR:
            case ContextType.LEFT_NAVBAR:
            case ContextType.RIGHT_NAVBAR:
                this.updateHotkeys();
                break;
            case ContextType.POPUP:
                ContextManager.actions = PopupContext.getActions();
                break;
            case ContextType.DROPDOWN:
                ContextManager.actions = [];
                break;
            default:
                ContextManager.actions = [];
                break;
        }
    }

    public static restoreCtx(): void {
        const previous = ContextManager.contextHistory.pop();
        if (previous !== undefined) {
            ContextManager.updateCtx(previous);
        }
    }

    private static onDown(event: KeyboardEvent): void {
        const target = event.target as HTMLElement | null;
        if (target?.closest?.("input, textarea, [contenteditable]")) {
            return;
        }

        const mainKey = ContextManager.getMainKeyFromEvent(event);
        if (!mainKey || MODIFIER_KEYS.has(event.key)) {
            return;
        }

        ContextManager.execute(event);
    }

    public static onFocus() {
        // no-op: combo is derived from each keydown event
    }

    private static execute(event: KeyboardEvent): void {
        const activeCtx = GeneralSelector.getActiveContext();

        if (activeCtx === ContextType.POPUP) {
            ContextManager.runMatchingActions(event, PopupContext.getActions());
            return;
        }

        if (activeCtx === ContextType.DROPDOWN) {
            return;
        }

        if (ContextManager.actions.length === 0) {
            ContextManager.updateHotkeys();
        }

        ContextManager.runMatchingActions(event, ContextManager.actions);
    }

    private static runMatchingActions(event: KeyboardEvent, actions: HotKeyAction[]): void {
        const combo = ContextManager.getActiveComboFromEvent(event);

        for (let i = 0; i < actions.length; i++) {
            const hotKey = actions[i];
            if (ContextManager.matchCombo(combo, hotKey.keyCombo)) {
                try {
                    if (typeof hotKey.action === "function") {
                        hotKey.action(event);
                        requestAnimationFrame(() => {
                            EditorActions.fullRender();
                        });
                    } else {
                        console.warn("Hotkey action is not a function", hotKey);
                    }
                } catch (err) {
                    console.error("Hotkey action failed:", hotKey, err);
                }
                break;
            }
        }
    }

    private static getActiveComboFromEvent(event: KeyboardEvent): string[] {
        const combo: string[] = [];

        if (event.ctrlKey || event.metaKey) {
            combo.push("Control");
        }
        if (event.altKey) {
            combo.push("Alt");
        }
        if (event.shiftKey) {
            combo.push("Shift");
        }

        const mainKey = ContextManager.getMainKeyFromEvent(event);
        if (mainKey) {
            combo.push(mainKey);
        }

        return combo;
    }

    private static getMainKeyFromEvent(event: KeyboardEvent): string | null {
        if (event.key === " ") {
            return " ";
        }

        if (
            event.key === "Escape" ||
            event.key === "Enter" ||
            event.key === "Delete" ||
            event.key === "Backspace" ||
            event.key.startsWith("Arrow")
        ) {
            return event.key;
        }

        if (event.code.startsWith("Digit")) {
            return event.code.slice(-1);
        }

        if (event.code.startsWith("Numpad") && event.code.length === 7) {
            return event.code.slice(-1);
        }

        if (event.altKey && ["Digit", "Key"].some((v) => event.code.includes(v))) {
            return event.code.slice(-1).toLowerCase();
        }

        if (event.key.length === 1) {
            return event.key.toLowerCase();
        }

        return event.key;
    }

    private static normalizeCombo(combo: string[]): string[] {
        return combo.map((key) => {
            if (key === "Meta") {
                return "Control";
            }
            if (key.length === 1) {
                return key.toLowerCase();
            }
            return key;
        });
    }

    private static matchCombo(combo1: string[], combo2: string[]): boolean {
        return isEmpty(
            xor(
                ContextManager.normalizeCombo(combo1),
                ContextManager.normalizeCombo(combo2)
            )
        );
    }
}
